import { Button, ButtonProps } from '@mui/material';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { ErrorMsg } from '../../../components/error/ErrorMsg';
import { Modal, ModalProps } from '../base/Modal';

/** Maps legacy Btn type to MUI variant and color. */
function mapVariant(type?: string): Pick<ButtonProps, 'variant' | 'color'> {
  switch (type) {
    case 'primary':
      return { variant: 'contained', color: 'primary' };
    case 'outline-primary':
      return { variant: 'outlined', color: 'primary' };
    case 'outline-secondary':
      return { variant: 'outlined', color: 'secondary' };
    case 'danger':
      return { variant: 'contained', color: 'error' };
    case 'link':
      return { variant: 'text' };
    case 'light':
      return { variant: 'outlined' };
    default:
      return { variant: 'contained', color: 'primary' };
  }
}

interface Props extends ModalProps {
  prompt: React.ReactNode;
  onConfirm: () => Promise<void> | void;
  confirmType?: string;
  cancelType?: string;
  confirmLabel?: React.ReactNode;
  cancelLabel?: React.ReactNode;
}

export const ConfirmationModal = (props: Props) => {
  const { onConfirm, onHide, prompt, confirmLabel, cancelLabel, confirmType, cancelType, ...rest } =
    props;
  const [confirmRunning, setConfirmRunning] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  function cleanupAndHide() {
    setError(null);
    onHide();
  }

  const confirmVariant = mapVariant(confirmType);
  const cancelVariant = mapVariant(cancelType);

  const footer = (
    <div>
      <Button
        variant={confirmVariant.variant}
        color={confirmVariant.color as any}
        onClick={async () => {
          setConfirmRunning(true);

          try {
            await onConfirm();
            cleanupAndHide();
          } catch (err: any) {
            setError(err);
          } finally {
            setConfirmRunning(false);
          }
        }}
        sx={{ marginRight: 1.5, minWidth: '8rem' }}
        data-cy="modal-confirm"
      >
        {confirmLabel || (
          <FormattedMessage
            id="common.confirmationModal.confirmBtnText"
            defaultMessage="Confirmar"
          />
        )}
      </Button>
      <Button
        variant={cancelVariant.variant}
        color={cancelVariant.color as any}
        onClick={cleanupAndHide}
        disabled={confirmRunning}
        data-cy="modal-cancel"
      >
        {cancelLabel || (
          <FormattedMessage id="common.confirmationModal.cancelBtnText" defaultMessage="Cancelar" />
        )}
      </Button>
    </div>
  );

  return (
    <Modal
      {...rest}
      onHide={cleanupAndHide}
      footer={footer}
      children={
        <p className="pt-3 pb-2">
          {prompt}
          {error && <ErrorMsg className="mt-3 mb-0" err={error} />}
        </p>
      }
    />
  );
};

ConfirmationModal.defaultProps = {
  confirmType: 'primary',
  cancelType: 'outline-secondary',
};
